"use client";

import { useState, useMemo, useCallback, useTransition } from "react";
import { useRouter } from "next/navigation";
import { PropertyCard } from "@/components/properties/property-card";
import { PropertyMap } from "@/components/properties/property-map";
import { PropertySearchBar } from "@/components/properties/property-search-bar";
import { SearchContextBanner } from "@/components/properties/search-context-banner";
import { SearchEmptyStates } from "@/components/properties/search-empty-states";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { motion, AnimatePresence } from "motion/react";
import { Map as MapIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PropertyWithCommunity, PropertyType, SearchContext } from "@/types/database";
import { TYPE_LABELS } from "@/types/database";

interface PropertiesListProps {
  initialProperties: PropertyWithCommunity[];
  searchContext?: SearchContext | null;
  initialQuery?: string | null;
  /** Raw query string from searchContext — used as React key to reset search bar on each submission */
  searchKey?: string;
  autoFocusSearch?: boolean;
}

// Labels for filter trigger when value is "all" or a specific option
const PRICE_LABELS: Record<string, string> = {
  "all": "Price Range",
  "under-3m": "Under AED 3M",
  "3m-5m": "AED 3M - 5M",
  "5m-10m": "AED 5M - 10M",
  "10m-plus": "Above AED 10M",
};

const BEDROOM_LABELS: Record<string, string> = {
  "all": "Bedrooms",
  "1": "1 Bedroom",
  "2": "2 Bedrooms",
  "3": "3 Bedrooms",
  "4+": "4+ Bedrooms",
};

function PropertyCardSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="aspect-[16/10] w-full rounded-xl" />
      <div className="border-t border-primary/10 pt-6 space-y-3">
        <Skeleton className="h-6 w-3/5 rounded-md" />
        <Skeleton className="h-3 w-2/5 rounded-md" />
        <Skeleton className="h-4 w-1/3 mt-4 rounded-md" />
      </div>
    </div>
  );
}

export function PropertiesList({
  initialProperties,
  searchContext = null,
  initialQuery = null,
  searchKey,
  autoFocusSearch = false,
}: PropertiesListProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [activePropertyId, setActivePropertyId] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    type: "all",
    price: "all",
    bedrooms: "all",
    location: "all",
  });

  // Determine if we're in AI search mode
  const isAISearchMode = searchContext !== null && searchContext.intent !== 'browse_all';
  const hasActiveFilters = filters.type !== "all" || filters.price !== "all" || filters.bedrooms !== "all" || filters.location !== "all";

  const onCardHover = useCallback((id: string | null) => {
    setActivePropertyId(id);
  }, []);

  const onMarkerHover = useCallback((id: string) => {
    setActivePropertyId(id);
  }, []);

  const onMarkerLeave = useCallback(() => {
    setActivePropertyId(null);
  }, []);

  // Handle search submission with transition (enables loading state)
  const handleSearch = useCallback((query: string) => {
    startTransition(() => {
      router.push(`/properties?q=${encodeURIComponent(query)}`);
    });
  }, [router, startTransition]);

  // Handle dropdown filter change — exit AI search mode if active
  const handleFilterChange = useCallback((filterKey: string, value: string | null) => {
    setFilters(prev => ({ ...prev, [filterKey]: value ?? "all" }));

    // If in AI search mode, exit it by navigating to /properties (no ?q=)
    if (isAISearchMode || initialQuery) {
      startTransition(() => {
        router.push('/properties');
      });
    }
  }, [isAISearchMode, initialQuery, router, startTransition]);

  // Handle search bar typing — reset dropdown filters if any are active
  const handleQueryChange = useCallback((q: string) => {
    if (q.length > 0 && hasActiveFilters) {
      setFilters({ type: "all", price: "all", bedrooms: "all", location: "all" });
    }
  }, [hasActiveFilters]);

  // Extract unique options from properties (for dropdown filters)
  const locations = useMemo(() =>
    Array.from(new Set(initialProperties.map(p => p.location_name).filter(Boolean))).sort(),
    [initialProperties]
  );

  const propertyTypes = useMemo(() =>
    Array.from(new Set(initialProperties.map(p => p.type).filter(Boolean))).sort(),
    [initialProperties]
  );

  // Apply client-side filters (only in browse mode, not AI search mode)
  const filteredProperties = useMemo(() => {
    // In AI search mode, properties are already filtered server-side
    if (isAISearchMode) return initialProperties;

    return initialProperties.filter((property) => {
      // Type Filter
      if (filters.type !== "all" && property.type !== filters.type) return false;

      // Location Filter
      if (filters.location !== "all") {
        if (property.location_name !== filters.location) return false;
      }

      // Bedrooms Filter
      if (filters.bedrooms !== "all") {
        const beds = property.bedrooms || 0;
        if (filters.bedrooms === "4+") {
          if (beds < 4) return false;
        } else {
          if (beds !== parseInt(filters.bedrooms)) return false;
        }
      }

      // Price Filter (price_from is in actual value, e.g., 4500000)
      if (filters.price !== "all") {
        // Fail fast if price_from is missing
        if (property.price_from == null) return false;

        const priceInMillions = property.price_from / 1_000_000;
        switch (filters.price) {
          case "under-3m":
            if (priceInMillions >= 3) return false;
            break;
          case "3m-5m":
            if (priceInMillions < 3 || priceInMillions > 5) return false;
            break;
          case "5m-10m":
            if (priceInMillions < 5 || priceInMillions > 10) return false;
            break;
          case "10m-plus":
            if (priceInMillions < 10) return false;
            break;
        }
      }

      return true;
    });
  }, [initialProperties, filters, isAISearchMode]);

  // Determine if we should show edge case empty states
  const showSearchEmptyState =
    isAISearchMode &&
    (searchContext.intent === 'unsupported' ||
      searchContext.intent === 'cannot_parse' ||
      (searchContext.intent === 'property_search' && searchContext.resultCount === 0));

  return (
    <>
      {/* Header Content - Always Visible */}
      <div className={cn(
        "w-full transition-all duration-500 ease-in-out px-6 md:px-12",
        isMapOpen ? "max-w-full" : "max-w-[1400px] mx-auto"
      )}>
        {/* Hero / Header */}
        <div className="mb-12 md:mb-16 text-center space-y-8">
          <div className="mx-auto max-w-4xl">
            <h1 className="font-serif italic text-3xl md:text-5xl lg:text-6xl text-primary leading-[0.9] text-center">
              Curated Living
            </h1>
            <p className="text-base md:text-lg text-muted-foreground font-sans leading-relaxed mt-8 text-center max-w-2xl mx-auto">
              Explore our exclusive portfolio of premium residences, where
              architectural brilliance meets unparalleled comfort in the most
              coveted locations.
            </p>
          </div>
        </div>

        {/* Natural Language Search Bar */}
        <div className="mb-10 md:mb-12">
          <PropertySearchBar
            key={searchKey ?? 'browse'}
            initialQuery={initialQuery}
            isSearching={isPending}
            autoFocus={autoFocusSearch}
            onSearch={handleSearch}
            onQueryChange={handleQueryChange}
          />
        </div>

        {/* Inline guidance for vague queries (browse_all from search) */}
        {searchContext?.intent === 'browse_all' && initialQuery && (
          <div className="text-center mb-6">
            <p className="text-xs text-muted-foreground">
              Try adding a location, budget, or property type for more specific results
            </p>
          </div>
        )}

        {/* Dropdown Filters */}
        <div className="mb-10 md:mb-16 flex flex-wrap gap-4 justify-center items-center transition-all duration-300">
          {/* Property Type */}
          <Select
            value={filters.type}
            onValueChange={(val) => handleFilterChange('type', val)}
          >
            <SelectTrigger className="w-[180px] rounded-xl border-primary/20 bg-background" aria-label="Property Type">
              <span className="flex flex-1 text-left">
                {filters.type === "all" ? "Property Type" : TYPE_LABELS[filters.type as PropertyType] || filters.type}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Property Type</SelectItem>
              {propertyTypes.map(type => (
                <SelectItem key={type} value={type}>{TYPE_LABELS[type]}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Price Range */}
          <Select
            value={filters.price}
            onValueChange={(val) => handleFilterChange('price', val)}
          >
            <SelectTrigger className="w-[180px] rounded-xl border-primary/20 bg-background" aria-label="Price Range">
              <span className="flex flex-1 text-left">
                {PRICE_LABELS[filters.price] ?? "Price Range"}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Price Range</SelectItem>
              <SelectItem value="under-3m">Under AED 3M</SelectItem>
              <SelectItem value="3m-5m">AED 3M - 5M</SelectItem>
              <SelectItem value="5m-10m">AED 5M - 10M</SelectItem>
              <SelectItem value="10m-plus">Above AED 10M</SelectItem>
            </SelectContent>
          </Select>

          {/* Bedrooms */}
          <Select
            value={filters.bedrooms}
            onValueChange={(val) => handleFilterChange('bedrooms', val)}
          >
            <SelectTrigger className="w-[180px] rounded-xl border-primary/20 bg-background" aria-label="Bedrooms">
              <span className="flex flex-1 text-left">
                {BEDROOM_LABELS[filters.bedrooms] ?? "Bedrooms"}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Bedrooms</SelectItem>
              <SelectItem value="1">1 Bedroom</SelectItem>
              <SelectItem value="2">2 Bedrooms</SelectItem>
              <SelectItem value="3">3 Bedrooms</SelectItem>
              <SelectItem value="4+">4+ Bedrooms</SelectItem>
            </SelectContent>
          </Select>

          {/* Location */}
          <Select
            value={filters.location}
            onValueChange={(val) => handleFilterChange('location', val)}
          >
            <SelectTrigger className="w-[180px] rounded-xl border-primary/20 bg-background" aria-label="Location">
              <span className="flex flex-1 text-left">
                {filters.location === "all" ? "Location" : filters.location}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Location</SelectItem>
              {locations.map(location => (
                <SelectItem key={location} value={location}>{location}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Reset Filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              onClick={() => setFilters({ type: "all", price: "all", bedrooms: "all", location: "all" })}
              className="text-xs text-muted-foreground hover:text-primary"
            >
              Reset Filters
            </Button>
          )}
        </div>

        {/* Search Context Banner (AI search mode) */}
        {searchContext && searchContext.intent === 'property_search' && searchContext.resultCount > 0 && (
          <SearchContextBanner searchContext={searchContext} />
        )}

        {/* List Header Placeholder (Sort/Count) - Only visible when map is open */}
        {isMapOpen && (
          <div className="flex justify-end items-center mb-6 animate-in fade-in duration-700 max-w-[1400px] mx-auto">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Sort by Price</span>
              <span className="text-sm font-bold text-primary cursor-pointer hover:opacity-70 transition-opacity">Select</span>
            </div>
          </div>
        )}
      </div>

      {/* Edge case empty states (hidden during loading) */}
      {!isPending && showSearchEmptyState && searchContext && (
        <div className="w-full max-w-[1400px] mx-auto px-6 md:px-12">
          <SearchEmptyStates searchContext={searchContext} />
        </div>
      )}

      {/* Split View Container - Show during loading or when not in an edge case empty state */}
      {(isPending || !showSearchEmptyState) && (
        <div className={cn(
          "w-full transition-all duration-500 ease-in-out",
          isMapOpen ? "flex flex-col md:flex-row gap-8 px-6 md:px-12 h-[800px]" : "px-6 md:px-12 max-w-[1400px] mx-auto"
        )}>
          {/* Properties Grid */}
          <div className={cn(
            "transition-all duration-500 ease-in-out",
            isMapOpen ? "w-full md:w-[60%] overflow-y-auto p-2 pr-4 scrollbar-hide" : "w-full"
          )}>
            {isPending ? (
              <div className={cn(
                "grid gap-y-16 gap-x-8",
                isMapOpen ? "grid-cols-1 xl:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              )}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <PropertyCardSkeleton key={`skeleton-${i}`} />
                ))}
              </div>
            ) : filteredProperties.length > 0 ? (
              <div className={cn(
                "grid gap-y-16 gap-x-8",
                isMapOpen ? "grid-cols-1 xl:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              )}>
                {filteredProperties.map((property, index) => (
                  <motion.div
                    key={property.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    layout={isMapOpen}
                  >
                    <PropertyCard
                      property={property}
                      onMouseEnter={() => onCardHover(String(property.id))}
                      onMouseLeave={() => onCardHover(null)}
                      isHighlighted={activePropertyId === String(property.id)}
                    />
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <h3 className="text-2xl font-serif text-primary mb-2">No properties found</h3>
                <p className="text-muted-foreground">Try adjusting your filters to see more results.</p>
                <Button
                  variant="link"
                  onClick={() => setFilters({ type: "all", price: "all", bedrooms: "all", location: "all" })}
                  className="mt-4 text-primary"
                >
                  Clear all filters
                </Button>
              </div>
            )}
          </div>

          {/* Map Panel - Only rendered when open */}
          <AnimatePresence mode="wait">
            {isMapOpen && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.4 }}
                className="hidden md:block w-[40%] h-full sticky top-24 rounded-xl overflow-hidden shadow-lg border border-primary/10"
              >
                <PropertyMap
                  properties={filteredProperties}
                  onClose={() => setIsMapOpen(false)}
                  className="h-full w-full"
                  activePropertyId={activePropertyId}
                  onMarkerHover={onMarkerHover}
                  onMarkerLeave={onMarkerLeave}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Floating Toggle Button - Always visible to toggle map */}
      {!showSearchEmptyState && (
        <div className="fixed bottom-8 right-8 z-40">
          <Button
            onClick={() => setIsMapOpen(!isMapOpen)}
            size="lg"
            className="rounded-xl h-14 w-14 md:h-12 md:w-auto md:px-8 shadow-2xl bg-background text-primary border border-primary hover:bg-primary hover:text-white transition-all duration-300 flex items-center justify-center gap-2 group"
          >
            {isMapOpen ? <X className="w-5 h-5" /> : <MapIcon className="w-5 h-5 group-hover:rotate-12 transition-transform" />}
            <span className="hidden md:inline font-bold uppercase tracking-widest text-xs">
              {isMapOpen ? "Close Map" : "Map View"}
            </span>
          </Button>
        </div>
      )}
    </>
  );
}
